import { Badge, ButtonIcon } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchWorkspaceJournals } from 'src/api/queries';
import {
  Box,
  Flex,
  Image,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import SubmissionLoader from '../Submissions/SubmissionLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useWorkspace } from './Users/userHook';
import ScrollToTopButton from '../../public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import JournalActionMenu from './JournalActionMenu';
import Footer from '../../../../components/Footer';
import { formatDate } from 'src/utils/date';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

export const renderBadgeTypes = (items: string[]) => {
  const more = items.slice(3);
  return (
    <Flex wrap="wrap" gap={3}>
      <Text>{items && items.slice(0, 3).join(', ')}</Text>

      {!!more.length && (
        <Tooltip hasArrow borderRadius={4} label={more.join(', ')}>
          <Box>
            <Badge size="context" variant="neutral">
              +{more.length}
            </Badge>
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

const SubmissionContexts = () => {
  const { workspaceId, workspaceShortname: shortname } = useWorkspace();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const offset = (page - 1) * pageSize;

  const { data, isLoading } = useQuery({
    queryKey: ['workspace-journals', { workspaceId, page, pageSize }],
    queryFn: () =>
      workspaceId ? fetchWorkspaceJournals(workspaceId, offset, pageSize) : null
  });

  const totalJournals = data?.total ?? 0;

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box height="100%">
        <Box padding={5} minHeight="100%">
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="flex-start"
            gap={4}
          >
            <Skeleton isLoaded={!isLoading}>
              <Flex alignItems={'center'} gap={2} height={5}>
                <Text textStyle="textBody">
                  Showing{' '}
                  <strong>
                    {totalJournals} journal
                    {totalJournals === 1 ? '' : 's'}
                  </strong>
                </Text>
              </Flex>
            </Skeleton>

            <Box width="100%">
              {isLoading ? (
                <SubmissionLoader
                  title="Loading Search Results"
                  subTitle="We are retrieving your search results. This should only take a moment."
                  bgColor="neutralSecondaryFill"
                />
              ) : data?.collection && data?.collection.length !== 0 ? (
                <Box
                  overflowX="auto"
                  overflowY="auto"
                  width="100%"
                  sx={{
                    '&::-webkit-scrollbar': {
                      height: '6px',
                      display: 'block'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '4px'
                    },
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {/* @ts-ignore */}
                  <Table width="1010px" minWidth="100%">
                    <Thead>
                      <Tr
                        data-testId="journals-table-header"
                        sx={{
                          '& th': {
                            textTransform: 'none',
                            color: 'black',
                            background: 'white'
                          }
                        }}
                      >
                        <Th>
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Journal
                          </Text>
                        </Th>

                        <Th>
                          <Text textStyle="headingItem">
                            Supported Article Types
                          </Text>
                        </Th>
                        <Th display="none">
                          <Flex gap={2} alignItems="center">
                            <Text textStyle="headingItem">Transfers To</Text>
                            <Tooltip
                              hasArrow
                              placement="top"
                              borderRadius={4}
                              shouldWrapChildren
                              label="Journals that can receive submissions through a “Reject and Transfer” editorial decision"
                            >
                              <ButtonIcon
                                size="small"
                                ariaLabel="Help Icon"
                                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              />
                            </Tooltip>
                          </Flex>
                        </Th>
                        <Th>
                          <Text textStyle="headingItem">Key Dates</Text>
                        </Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data?.collection.map(journal => (
                        <Tr
                          key={journal.id}
                          data-testId="journals-table-row"
                          gap={4}
                          background="white"
                        >
                          <Td
                            onClick={() =>
                              navigate(
                                `/app/workspace/${shortname}/submissions?journalId=${journal.id}`
                              )
                            }
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            maxWidth={'500px'}
                          >
                            <Flex flexDirection="row" gap={2}>
                              <Flex
                                width="125px"
                                height="50px"
                                borderRadius="md"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Image
                                  src={journal.config?.logoUrl}
                                  alt={`${
                                    journal.data?.name ?? 'Journal'
                                  } logo`}
                                  width="100%"
                                  height="100%"
                                  objectFit="contain"
                                  fallback={
                                    <Text textStyle="headingLine" color="grey">
                                      {'No Logo'}
                                    </Text>
                                  }
                                />
                              </Flex>

                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={'View Submissions for this journal'}
                              >
                                <Flex
                                  direction="column"
                                  alignItems="flex-start"
                                  justifyContent="flex-start"
                                  width="100%"
                                  minWidth={0}
                                  textAlign="left"
                                >
                                  <Box width="100%" overflow="hidden">
                                    <Text
                                      wordBreak="break-word"
                                      textStyle="headingLine"
                                      color="actionPrimaryFill"
                                      textDecoration="none"
                                      _hover={{
                                        textDecoration: 'underline',
                                        textDecorationColor: 'actionPrimaryFill'
                                      }}
                                    >
                                      {journal.data.name}
                                    </Text>
                                  </Box>
                                  <Flex
                                    alignItems="flex-start"
                                    flexDirection="column"
                                  >
                                    <Text
                                      textTransform="uppercase"
                                      fontWeight="bold"
                                    >
                                      {journal.data.shortname}
                                    </Text>
                                    <Text color="grey">
                                      {journal.submissionCount ?? 0} Submission
                                      {(journal.submissionCount ?? 0) > 1
                                        ? 's'
                                        : ''}
                                    </Text>
                                  </Flex>
                                </Flex>
                              </Tooltip>
                            </Flex>
                          </Td>
                          <Td maxWidth="220px">
                            <Text>
                              {renderBadgeTypes(journal.articleTypes ?? [])}
                            </Text>
                          </Td>
                          <Td maxWidth="220px" display="none">
                            <Text>
                              {renderBadgeTypes(journal.transferJournals ?? [])}
                            </Text>
                          </Td>

                          <Td textStyle="textContext">
                            <Flex flexDirection="column" gap={1}>
                              <Text>
                                <strong>Activated:</strong>{' '}
                                {journal.config.activationDate
                                  ? formatDate(
                                      new Date(journal.config.activationDate)
                                    )
                                  : journal.createdAt
                                  ? formatDate(new Date(journal.createdAt))
                                  : 'N/A'}
                              </Text>
                              <Text>
                                <strong>Last Updated:</strong>{' '}
                                {journal.modifiedAt
                                  ? formatDate(new Date(journal.modifiedAt))
                                  : 'N/A'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td>
                            <JournalActionMenu
                              journalId={journal.id}
                              handleClick={journalId =>
                                navigate(
                                  `/app/workspace/${shortname}/submissions?journalId=${journalId}`
                                )
                              }
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <SubmissionLoader
                  title="No Results Found"
                  subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
                  bgColor="neutralSecondaryFill"
                />
              )}

              <TablePagination
                ariaLabel="Journals pagination"
                currentPage={page}
                totalItems={totalJournals}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </Box>
          </Flex>
        </Box>
        <Footer />
      </Box>
      <ScrollToTopButton />
    </Box>
  );
};

export default SubmissionContexts;
